import React, { useState } from 'react';
import { AlertTriangle, Phone, Pill, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { Client } from '../models/Client';

interface EmergencyInfoProps {
  client: Client;
}

export const EmergencyInfo: React.FC<EmergencyInfoProps> = ({ client }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasEmergencyContacts = client.emergencyContacts && client.emergencyContacts.length > 0;
  const hasAllergies = client.allergies && client.allergies.length > 0;
  const hasMedications = client.medications && client.medications.length > 0;
  const hasMedicalConditions = client.medicalConditions && client.medicalConditions.length > 0;
  const hasSafetyProtocol = client.safetyProtocol && client.safetyProtocol.trim();

  const hasAnyEmergencyInfo = hasEmergencyContacts || hasAllergies || hasMedications || hasMedicalConditions || hasSafetyProtocol;

  if (!hasAnyEmergencyInfo) {
    return null;
  }

  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg overflow-hidden">
      {/* Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-red-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
          <div className="text-left">
            <h3 className="text-lg font-bold text-red-900">Emergency Information</h3>
            <p className="text-xs text-red-700">
              Critical safety and medical information - Tap to {isExpanded ? 'hide' : 'view'}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-red-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-red-600" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t-2 border-red-300 p-4 space-y-4 bg-white">
          {/* Emergency Contacts */}
          {hasEmergencyContacts && (
            <div>
              <h4 className="text-sm font-bold text-red-900 mb-3 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Emergency Contacts
              </h4>
              <div className="space-y-2">
                {client.emergencyContacts!.map((contact, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border-2 ${
                      contact.isPrimary
                        ? 'bg-red-100 border-red-400'
                        : 'bg-gray-50 border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-gray-900">{contact.name}</span>
                      {contact.isPrimary && (
                        <span className="text-xs px-2 py-1 bg-red-600 text-white rounded-full font-medium">
                          PRIMARY
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">
                      <span className="font-medium">{contact.relationship}</span>
                    </div>
                    <a
                      href={`tel:${contact.phone}`}
                      className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-800 mt-2"
                    >
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Allergies */}
          {hasAllergies && (
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3">
              <h4 className="text-sm font-bold text-yellow-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                ALLERGIES
              </h4>
              <ul className="space-y-1">
                {client.allergies!.map((allergy, idx) => (
                  <li key={idx} className="text-sm text-yellow-900 flex items-start gap-2">
                    <AlertTriangle className="w-3 h-3 text-yellow-600 mt-1 flex-shrink-0" />
                    <span className="font-medium">{allergy}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Medications */}
          {hasMedications && (
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3">
              <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                <Pill className="w-4 h-4" />
                Current Medications
              </h4>
              <ul className="space-y-1">
                {client.medications!.map((medication, idx) => (
                  <li key={idx} className="text-sm text-blue-900 flex items-start gap-2">
                    <Pill className="w-3 h-3 text-blue-600 mt-1 flex-shrink-0" />
                    <span>{medication}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Medical Conditions */}
          {hasMedicalConditions && (
            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
              <h4 className="text-sm font-bold text-purple-900 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                Medical Conditions
              </h4>
              <ul className="space-y-1">
                {client.medicalConditions!.map((condition, idx) => (
                  <li key={idx} className="text-sm text-purple-900 flex items-start gap-2">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>{condition}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Safety Protocol */}
          {hasSafetyProtocol && (
            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-3">
              <h4 className="text-sm font-bold text-orange-900 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                Safety Protocol
              </h4>
              <p className="text-sm text-orange-900 whitespace-pre-wrap">{client.safetyProtocol}</p>
            </div>
          )}

          {/* Footer Notice */}
          <div className="bg-red-100 border-2 border-red-400 rounded-lg p-3 text-center">
            <p className="text-xs font-bold text-red-900">
              In case of emergency, call 911 immediately
            </p>
            <p className="text-xs text-red-700 mt-1">
              Then contact primary emergency contact and supervisor
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
